import { merge } from 'lodash';
import { memo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, FormControl, FormHelperText, SxProps, Theme } from '@common-components';
import { messages } from 'i18n';
import { FormControlHelperTextMarginBottom, FormControlPaddingBottom } from 'themes';
import { normalizeError } from 'utils';
import { Editor } from 'components/Editor/Editor';
import InputLabel from './InputLabel';
import { FormHelperTextStyles, FormWYSIWYGStyles } from './styles';

export interface FormEditorProps {
  name: string;
  label?: string;
  optional?: boolean;
  disabled?: boolean;
  placeholder?: string;
  sxOverride?: SxProps<Theme>;
  autoFocus?: boolean;
  hideOptionalLabel?: boolean;
  borderColor?: string;
  helperTextContent?: JSX.Element;
  paddingBottom?: number;
  excludeToolbarProps?: string[];
  defaultValue?: string;
}

const FormEditor = memo(
  ({
    name,
    label = '',
    optional,
    disabled,
    placeholder = messages.general.inputPlaceholder,
    sxOverride = {},
    autoFocus = false,
    hideOptionalLabel,
    borderColor,
    helperTextContent,
    excludeToolbarProps,
    paddingBottom = FormControlPaddingBottom,
    defaultValue,
  }: FormEditorProps) => {
    const {
      control,
      formState: { errors },
    } = useFormContext();

    const [isFocus, setIsFocus] = useState(false);
    const [isHover, setIsHover] = useState(false);

    const errorMessage = normalizeError(errors, name);

    return (
      <FormControl
        fullWidth
        sx={{ pb: paddingBottom, mb: errorMessage ? FormControlHelperTextMarginBottom : 0, height: '100%' }}
      >
        <InputLabel
          label={label}
          error={!!errorMessage}
          optional={optional}
          htmlFor={name}
          hideOptionalLabel={hideOptionalLabel}
        />
        <Controller
          defaultValue={defaultValue}
          control={control}
          name={name}
          render={({ field: { onChange, value, onBlur } }) => (
            <Box
              className="cap-form-editor"
              display="flex"
              flexDirection="column"
              height="100%"
              sx={merge(FormWYSIWYGStyles(!!errorMessage, isFocus, isHover, disabled, borderColor), sxOverride)}
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
            >
              <Editor
                autoFocus={autoFocus}
                editorContent={value}
                setEditorContent={onChange}
                disabled={disabled}
                placeholder={placeholder}
                onFocus={() => setIsFocus(true)}
                onBlur={() => {
                  setIsFocus(false);
                  onBlur();
                }}
                excludeToolbarProps={excludeToolbarProps}
              />
            </Box>
          )}
        />
        {(errorMessage || helperTextContent) && (
          <FormHelperText sx={FormHelperTextStyles} error={!!errorMessage}>
            {errorMessage ?? helperTextContent}
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);

export default FormEditor;
